import { responsiveImage } from "../utils/responsiveImage";
import { ReactNode, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { Video } from "../components/Video";

const MAX_WIDTH = 800;
const LIST_ITEM_GAP = 1;

const S = {
  Root: styled.div`
    padding: 3vh 0;
    display: flex;
    flex-direction: column;
    gap: 3vh;
  `,

  Section: styled.section`
    display: flex;
    flex-direction: column;
    margin: 0 auto;
    max-width: ${MAX_WIDTH}px;
    width: 100%;
  `,

  SectionTitle: styled.div`
    max-width: 800px;
    line-height: 1.3;
    margin-bottom: 0.8vh;
    padding: 0 0.1em;
  `,

  SectionDescription: styled.div`
    max-width: 800px;
    line-height: 1.3;
    opacity: 0.5;
  `,

  WorkItemsList: styled.div`
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    width: 100%;
    gap: ${LIST_ITEM_GAP}vh;
  `,

  WorkItem: styled.div`
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    max-width: 100%;
  `,

  MotionGrid: styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${LIST_ITEM_GAP}vh;
    width: 100%;

    video {
      max-width: none;
      min-width: 0;
    }

    @media (max-width: 640px) {
      grid-template-columns: 1fr;
    }
  `,

  ButtonLink: styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;

    padding: 0.22em 0.8em 0.3em;
    border: 1.5px solid #000;
    border-radius: 999px;

    background: #000;
    color: #fff;

    font-size: 0.95em;
    text-decoration: none;
    line-height: 1;

    transition:
      background-color 220ms cubic-bezier(0.4, 0, 0.2, 1),
      color 220ms cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      background: #fff;
      color: #000;
    }
  `,

  WorkItemImage: styled.img.attrs((props) => ({
    loading: props.loading ?? "lazy",
    onLoad: (e: any) => e.target.classList.add("loaded"),
  }))<{ vertical?: boolean }>`
    width: ${MAX_WIDTH}px;
    max-width: 100%;
    display: block;
    height: auto;
    background: #f0f0f0;
    min-width: 20vw;
    opacity: 0.5;
    transition: opacity 100ms ease-in;

    &.loaded {
      opacity: 1;
    }

    ${(p) =>
      p.vertical &&
      css`
        max-width: 50%;
      `}
  `,

  FTBWorkItem: styled.div<{ $columns?: number }>`
    display: flex;
    justify-content: center;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 0;
    line-height: 0;
    max-width: 100%;
    overflow: hidden;

    ${(p) =>
      p.$columns &&
      css`
        > img {
          width: ${100 / p.$columns}%;
          max-width: ${100 / p.$columns}%;
          flex-basis: ${100 / p.$columns}%;
        }
      `}
  `,

  FTBImage: styled.img.attrs((props) => ({
    loading: props.loading ?? "lazy",
    onLoad: (e: any) => e.target.classList.add("loaded"),
  }))`
    width: ${MAX_WIDTH}px;
    max-width: 100%;
    height: auto;
    display: block;
    background: transparent;
    object-fit: contain;
    opacity: 0.5;
    transition: opacity 100ms ease-in;

    &.loaded {
      opacity: 1;
    }
  `,

  Divider: styled.div`
    &::after {
      content: "";
      display: block;
      border-bottom: 2px dotted #000;
      width: 20%;
      opacity: 0.3;
    }
  `,
  NameBlock: styled.div`
    max-width: 650px;
    line-height: 1.1;
    margin-bottom: 3vh;
    padding: 0 0.5em;
  `,
};

export const ScreenWorks = () => {
  return (
    <S.Root>
      <S.Section>
        <S.SectionTitle>
          Merchandise design for Saya Gray’s 2025 tour.
        </S.SectionTitle>

        <S.WorkItemsList>
          <S.WorkItem>
            <S.WorkItemImage
              vertical
              {...responsiveImage(
                "shot33.jpg",
                "(max-width: 800px) 50vw, 400px",
              )}
              alt="Bag"
              loading="eager"
              fetchPriority="high"
            />
            <S.WorkItemImage
              vertical
              {...responsiveImage(
                "shot35.jpg",
                "(max-width: 800px) 50vw, 400px",
              )}
              alt="inmybag"
              loading="eager"
            />
          </S.WorkItem>
          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "saya_ls22.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>

          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "saya_ls13.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>
          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "saya_bag.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>
          <S.WorkItem>
            <S.WorkItemImage
              vertical
              {...responsiveImage(
                "shot36.jpg",
                "(max-width: 800px) 50vw, 400px",
              )}
              alt="Bag"
            />
            <S.WorkItemImage
              vertical
              {...responsiveImage(
                "shot43.jpg",
                "(max-width: 800px) 50vw, 400px",
              )}
              alt="inmybag"
            />
          </S.WorkItem>
          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "saya_ss22.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>

          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "saya_ss1.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>

          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "Socks.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>
        </S.WorkItemsList>
      </S.Section>
      <S.Section>
        <S.SectionTitle>
          Landing page design and illustration for Saya Gray{" "}
          <S.ButtonLink
            href="https://sayagray.ca"
            target="_blank"
            rel="noopener noreferrer"
          >
            sayagray.ca
          </S.ButtonLink>
        </S.SectionTitle>
        <S.WorkItemsList>
          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "saya_laptop_01.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>
          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "saya_phone.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>
          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "saya_laptop_02.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>
        </S.WorkItemsList>
      </S.Section>
      <S.Section>
        <S.SectionTitle>
          Poster, title and credits for GROWN, a short film by Audrey Cicerello.
        </S.SectionTitle>
        <S.WorkItemsList>
          <S.WorkItem>
            <Video
              src={require("./images/Grown_title_1080_crop.mp4")}
              aspectRatio={1920 / 1012}
            />
          </S.WorkItem>
          <S.WorkItem>
            <Video
              src={require("./images/Grown_credits_1080.mp4")}
              aspectRatio={1920 / 1012}
            />
          </S.WorkItem>
          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "Grown_portfolio_03.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>
        </S.WorkItemsList>
      </S.Section>
      <S.Section>
        <S.SectionTitle>
          Brand identity and website design for Filling The Blank.
        </S.SectionTitle>
        <S.WorkItemsList>
          <S.FTBWorkItem>
            <S.FTBImage
              {...responsiveImage(
                "FTB_tote.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Filling The Blank tote"
            />
          </S.FTBWorkItem>

          <S.FTBWorkItem>
            <S.FTBImage
              {...responsiveImage(
                "FTB_A4.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Filling The Blank tote"
            />
          </S.FTBWorkItem>
          <S.FTBWorkItem>
            <S.FTBImage
              {...responsiveImage(
                "FTB_card.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Filling The Blank card"
            />
          </S.FTBWorkItem>
          <S.FTBWorkItem>
            <S.FTBImage
              {...responsiveImage(
                "FTB_tee.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Filling The Blank tote"
            />
          </S.FTBWorkItem>

          <S.FTBWorkItem>
            <S.FTBImage
              {...responsiveImage(
                "FTB_wesite_02.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Filling The Blank website preview"
            />
          </S.FTBWorkItem>
          <S.FTBWorkItem>
            <S.FTBImage
              {...responsiveImage(
                "FTB_wesite_ph_05.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Filling The Blank social preview"
            />
          </S.FTBWorkItem>
          <S.FTBWorkItem $columns={2}>
            <S.FTBImage
              {...responsiveImage(
                "FTB_Sarari - Dark_Orig.jpg",
                "(max-width: 800px) 50vw, 400px",
              )}
              alt="Filling The Blank desktop originals page"
            />
            <S.FTBImage
              {...responsiveImage(
                "FTB_Sarari - Dark_About.jpg",
                "(max-width: 800px) 50vw, 400px",
              )}
              alt="Filling The Blank desktop about and contact page"
            />
          </S.FTBWorkItem>
          <S.FTBWorkItem $columns={3}>
            <S.FTBImage
              {...responsiveImage(
                "FTBDark.jpg",
                "(max-width: 800px) 33.3333vw, 266.667px",
              )}
              alt="Filling The Blank mobile originals page"
            />
            <S.FTBImage
              {...responsiveImage(
                "FTBDark-1.jpg",
                "(max-width: 800px) 33.3333vw, 266.667px",
              )}
              alt="Filling The Blank mobile originals list"
            />
            <S.FTBImage
              {...responsiveImage(
                "FTBDark-3.jpg",
                "(max-width: 800px) 33.3333vw, 266.667px",
              )}
              alt="Filling The Blank mobile contact form"
            />
          </S.FTBWorkItem>
        </S.WorkItemsList>
      </S.Section>

      <S.Section>
        <S.SectionTitle>
          Motion graphics for Polyana’s social media.
        </S.SectionTitle>
        <S.MotionGrid>
          <Video
            src={require("./images/Polyana_flower_01.mp4")}
            aspectRatio={1080 / 1350}
          />

          <Video
            src={require("./images/Polyana_table.mp4")}
            aspectRatio={1080 / 1350}
          />
          <Video
            src={require("./images/Polyana_insta_website_30.mp4")}
            aspectRatio={1080 / 1350}
          />
          <Video
            src={require("./images/Comp 1.mp4")}
            aspectRatio={1080 / 1350}
          />
        </S.MotionGrid>
      </S.Section>
      <S.Section>
        <S.SectionTitle>
          Title and credit design for DISAPPEARS, a music video for John Maus.
          Directed by Blake James Reid and produced by Gummy films.
        </S.SectionTitle>
        <S.WorkItemsList>
          <S.WorkItem>
            <Video
              src={require("./images/JM_title_1080p.mp4")}
              aspectRatio={1920 / 1080}
            />
          </S.WorkItem>
          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage("JM.jpg", "(max-width: 800px) 100vw, 800px")}
              alt="Spider"
            />
          </S.WorkItem>
          <S.WorkItem>
            <Video
              src={require("./images/JM_credits_black.mp4")}
              aspectRatio={1920 / 1080}
            />
          </S.WorkItem>
        </S.WorkItemsList>
      </S.Section>

      <S.Section>
        <S.SectionTitle>
          Poster design for UNITY HOUSE, a short film by Daria Zapriagaeva.
        </S.SectionTitle>

        <S.WorkItemsList>
          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "UH_01.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>

          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "UH_02.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>
        </S.WorkItemsList>
      </S.Section>

      <S.Section>
        <S.SectionTitle>
          Promotional visual for dustBunny, an electronic music artist.
        </S.SectionTitle>

        <S.WorkItemsList>
          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "DB_11 1.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>
        </S.WorkItemsList>
      </S.Section>

      <S.Section>
        <S.SectionTitle>
          Poster, title and credits for NIFLHEIM, a short film by Max Naum.
        </S.SectionTitle>
        <S.WorkItemsList>
          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "NFL_Poster.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Logo"
            />
          </S.WorkItem>

          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "NFL_titl.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Logo"
            />
          </S.WorkItem>

          <S.WorkItem>
            <S.WorkItemImage
              src={require("./images/End_credits.gif")}
              alt="Logo"
              width={3000}
              height={1582}
            />
          </S.WorkItem>
        </S.WorkItemsList>
      </S.Section>

      <S.Section>
        <S.SectionTitle>
          Poster design and credits for The Man Without Qualities, a short film
          by Blake James Reid.
        </S.SectionTitle>

        <S.WorkItemsList>
          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "Poster_1.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>
          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "Bill_v5.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>
          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "Bill_poster.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>

          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "John.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>
          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "Brian.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>
          <S.WorkItem>
            <S.WorkItemImage
              src={require("./images/Credits.gif")}
              alt="Spider"
              width={2300}
              height={1213}
            />
          </S.WorkItem>
        </S.WorkItemsList>
      </S.Section>

      <S.Section>
        <S.SectionTitle>
          Brand identity and promotional materials for Burli & Perelotti, a
          cider brand.
        </S.SectionTitle>
        <S.WorkItemsList>
          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "Burli_pr_LOGO.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>
          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "Logo_variations.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>

          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "Burli_pr_page.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>
          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage("9+.jpg", "(max-width: 800px) 100vw, 800px")}
              alt="Spider"
            />
          </S.WorkItem>

          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "card.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>

          <S.WorkItem>
            <S.WorkItemImage
              vertical
              {...responsiveImage(
                "Insta_post1.jpg",
                "(max-width: 800px) 50vw, 400px",
              )}
              alt="Bag"
            />
            <S.WorkItemImage
              vertical
              {...responsiveImage(
                "Insta_post5.jpg",
                "(max-width: 800px) 50vw, 400px",
              )}
              alt="inmybag"
            />
            <S.WorkItemImage
              vertical
              {...responsiveImage(
                "Insta_post7.jpg",
                "(max-width: 800px) 50vw, 400px",
              )}
              alt="Trio"
            />
            <S.WorkItemImage
              vertical
              {...responsiveImage(
                "Insta_post3.jpg",
                "(max-width: 800px) 50vw, 400px",
              )}
              alt="Zhizh"
            />
          </S.WorkItem>

          <S.WorkItem>
            <S.WorkItemImage
              vertical
              {...responsiveImage("3-1.jpg", "(max-width: 800px) 50vw, 400px")}
            />
            <S.WorkItemImage
              vertical
              {...responsiveImage(
                "zhigs3.jpg",
                "(max-width: 800px) 50vw, 400px",
              )}
            />
          </S.WorkItem>

          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "Bottles_2.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>
          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "Logo_explorations.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>
          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "tshirt.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>
        </S.WorkItemsList>
      </S.Section>
      <S.Section>
        <S.SectionTitle>
          Poster design and AI-generated mood boards for a series of music
          events.
        </S.SectionTitle>

        <S.WorkItemsList>
          <S.WorkItem>
            <S.WorkItemImage
              vertical
              {...responsiveImage("telo.jpg", "(max-width: 800px) 50vw, 400px")}
              alt="Poster"
            />
            <S.WorkItemImage
              vertical
              {...responsiveImage(
                "poster.v4.jpg",
                "(max-width: 800px) 50vw, 400px",
              )}
              alt="Poster"
            />

            <BlinkSwitch
              variations={[
                <S.WorkItemImage
                  {...responsiveImage(
                    "sumka_6x9.jpg",
                    "(max-width: 800px) 100vw, 800px",
                  )}
                  alt="Sumka"
                />,
                <S.WorkItemImage
                  {...responsiveImage(
                    "girlz.jpg",
                    "(max-width: 800px) 100vw, 800px",
                  )}
                  alt="Girls"
                />,
              ]}
            />
          </S.WorkItem>
          <S.WorkItem>
            <S.WorkItemImage
              vertical
              {...responsiveImage(
                "bag_1.jpg",
                "(max-width: 800px) 50vw, 400px",
              )}
              alt="Bag"
            />
            <S.WorkItemImage
              vertical
              {...responsiveImage(
                "inmybag.jpg",
                "(max-width: 800px) 50vw, 400px",
              )}
              alt="inmybag"
            />
            <S.WorkItemImage
              vertical
              {...responsiveImage("trio.jpg", "(max-width: 800px) 50vw, 400px")}
              alt="Trio"
            />
            <S.WorkItemImage
              vertical
              {...responsiveImage(
                "zhizh.jpg",
                "(max-width: 800px) 50vw, 400px",
              )}
              alt="Zhizh"
            />
          </S.WorkItem>
          <S.WorkItem>
            <S.WorkItemImage
              vertical
              {...responsiveImage("silk.jpg", "(max-width: 800px) 50vw, 400px")}
              alt="Silk"
            />
            <S.WorkItemImage
              vertical
              {...responsiveImage("meh.jpg", "(max-width: 800px) 50vw, 400px")}
              alt="Fur"
            />
          </S.WorkItem>
          <S.WorkItem>
            <S.WorkItemImage
              vertical
              {...responsiveImage("mish.jpg", "(max-width: 800px) 50vw, 400px")}
              alt="Silk"
            />
            <S.WorkItemImage
              vertical
              {...responsiveImage(
                "girll.jpg",
                "(max-width: 800px) 50vw, 400px",
              )}
              alt="Fur"
            />
          </S.WorkItem>

          {/* 
          <S.WorkItem>
            <S.WorkItemImage {...responsiveImage("logo.jpg", "(max-width: 800px) 100vw, 800px")} alt="Logo" />
          </S.WorkItem> */}
        </S.WorkItemsList>
      </S.Section>

      <S.Section>
        <S.SectionTitle>Selected AI image experiments.</S.SectionTitle>
        <S.WorkItemsList>
          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage("bbs.jpg", "(max-width: 800px) 100vw, 800px")}
              alt="Logo"
            />
          </S.WorkItem>

          <S.WorkItem>
            <S.WorkItemImage
              vertical
              {...responsiveImage(
                "poster_no-text.jpg",
                "(max-width: 800px) 50vw, 400px",
              )}
              alt="Divan"
            />
            <S.WorkItemImage
              vertical
              {...responsiveImage(
                "skirt.jpg",
                "(max-width: 800px) 50vw, 400px",
              )}
              alt="Poster"
            />
          </S.WorkItem>

          <S.WorkItem>
            <S.WorkItemImage
              vertical
              {...responsiveImage(
                "armour.jpg",
                "(max-width: 800px) 50vw, 400px",
              )}
              alt="Bag"
            />
            <S.WorkItemImage
              vertical
              {...responsiveImage(
                "horse.jpg",
                "(max-width: 800px) 50vw, 400px",
              )}
              alt="inmybag"
            />
            <S.WorkItemImage
              vertical
              {...responsiveImage(
                "baggg.jpg",
                "(max-width: 800px) 50vw, 400px",
              )}
              alt="Trio"
            />
            <S.WorkItemImage
              vertical
              {...responsiveImage(
                "angel2.jpg",
                "(max-width: 800px) 50vw, 400px",
              )}
              alt="Zhizh"
            />
          </S.WorkItem>

          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "both.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="AI"
            />
          </S.WorkItem>

          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "zmei.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="AI"
            />
          </S.WorkItem>

          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "01 2.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="AI"
            />
          </S.WorkItem>

          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "shuba.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="AI"
            />
          </S.WorkItem>

          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage("02.jpg", "(max-width: 800px) 100vw, 800px")}
              alt="AI"
            />
          </S.WorkItem>
        </S.WorkItemsList>
      </S.Section>
      <S.Section>
        <S.SectionTitle>AI-assisted photo collages.</S.SectionTitle>
        <S.WorkItemsList>
          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "spina 2.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spina"
            />
          </S.WorkItem>

          <S.WorkItem>
            <S.WorkItemImage
              {...responsiveImage(
                "spider.jpg",
                "(max-width: 800px) 100vw, 800px",
              )}
              alt="Spider"
            />
          </S.WorkItem>

          <S.WorkItem>
            <S.WorkItemImage
              vertical
              {...responsiveImage("rap.jpg", "(max-width: 800px) 50vw, 400px")}
            />
            <S.WorkItemImage
              vertical
              {...responsiveImage(
                "grey_new.jpg",
                "(max-width: 800px) 50vw, 400px",
              )}
            />
          </S.WorkItem>
        </S.WorkItemsList>
      </S.Section>

      <S.Section>
        <S.NameBlock>
          Alesya Nesolenova
          <br />
          00alesyan@gmail.com
          <br />
          <img
            {...responsiveImage("l'etoile.jpg", "120px")}
            alt="Alesya Nesolenova"
            style={{ maxWidth: "120px", height: "auto", margin: "10px 0 0 0" }}
          />
        </S.NameBlock>
      </S.Section>
    </S.Root>
  );
};

const BlinkSwitch = ({
  variations,
  interval = 800,
}: {
  variations: ReactNode[];
  interval?: number;
}) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1) % variations.length);
    }, interval);

    return () => clearInterval(intervalId);
  }, [interval, variations.length]);

  return (
    <>
      {variations.map((variation, i) => (
        <div
          key={i}
          style={{
            display: i === index ? "block" : "none",
          }}
        >
          {variation}
        </div>
      ))}
    </>
  );
};
